import '@inovua/reactdatagrid-community/index.css'
import ReactDataGrid from '@inovua/reactdatagrid-community'
import React, { MutableRefObject, useCallback, useEffect, useState } from 'react';
import Button from "../../../../components/button/Button";
import "./bulkUsersImportTable.css";
import styles from "./bulkUsersImportTable.module.scss";
import Checkbox from "../../../../components/checkbox/Checkbox";
import { gql, useMutation } from "@apollo/client";
import { isNotEmail } from "../../../../utils/validation";
import { useToast } from "../../../../hooks/useToast";
import { isOdd } from "../../../../utils/helpers";
import { TypeComputedProps } from "@inovua/reactdatagrid-community/types";
import { FixDataWizard } from "./fixDataWizard";
import Spinner from "../../../../components/spinner/Spinner";
import { logger } from '../../../../utils/logger';

type jsonParseDataType = {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  account?: string;
  adminEmail: any[] | string;
  school?: string;
  role: string;
  invite: string;
}

type Props = {
  jsonParse: jsonParseDataType[];
  accountOptions: any;
  schoolOptions: any;
  closeSlide: any;
  isDistAdmin?: boolean;
};

const mutationCreateInvites = gql`
  mutation bulkInviteUsersAsEducator($input: [BulkUserInviteInput]) {
    bulkInviteUsersAsEducator(input: $input) {
      id
      firstName
      lastName
      email
    }
  }
`;

export type emailChecking = {
  rowId: number;
  column: string;
  fieldValue: string;
}

export type nameChecking = {
  rowId: number;
  column: string;
  fieldValue: string;
}

type MutationCreateInviteResponse = {
  bulkInviteUsersAsEducator: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  }[];
}

export type typoCheckingDataToClean = {
  checkingEmails: emailChecking[]
  checkingFirstName: nameChecking[]
  checkingSecondName: nameChecking[]
}

let tableCSS = {
  id: {
    width: 60
  },
  firstName: {
    width: 140
  },
  secondName: {
    width: 140
  },
  email: {
    width: 140
  },
  remove: {
    width: 90
  }
}

// @ts-ignore
const rowClassName = ({data}) => (isOdd(data.id)) ? 'odd-rows' : '';

let gridStyle = {
  maxHeight: 540,
  minHeight: 540
}


let stringCharExists = (value: string | undefined) => {
  return !(!value || value?.length === 0 || !value?.trim());
}

export const BulkUsersImportTable = (props: Props) => {
    const {jsonParse: uploadedData, closeSlide} = props;

    const [gridRef, setGridRef] = useState<MutableRefObject<TypeComputedProps | null> | null>(null);
    const [flagToClean, setFlagToClean] = useState<boolean | null>(false); // change to false
    const [dataSource, setDataSource] = useState<jsonParseDataType[]>([]);
    const [dataToClean, setDataToClean] = useState<typoCheckingDataToClean>({
      checkingFirstName: [],
      checkingSecondName: [],
      checkingEmails: []
    });
    const [success, setSuccess] = useState(false);
    const [duplicateEmailMessage, setDuplicateEmailMessage] = useState(false);
    const {successToast, errorToast} = useToast();

    const [bulkInviteUsers, {loading: bulkInviteProcessing}] = useMutation<MutationCreateInviteResponse>(mutationCreateInvites, {
      onError(err) {
        errorToast('Bulk upload invite failed, please check the developers console and contact customer support!');
        logger.info(err);
      },
    })

    const bulkUploadColumns = [
      {
        name: 'firstName', header: 'First Name * ', minWidth: tableCSS.firstName.width, headerEllipsis: false, flex: 1,
        headerProps: {
          className: "table-header"
        },
        render: ({value}) => (<span style={{marginLeft: 20, fontSize: 14}}>{value}</span>),
      },
      {
        name: 'lastName', header: 'Second Name *', minWidth: tableCSS.secondName.width, headerEllipsis: false, flex: 1,
        headerProps: {
          className: "table-header"
        },
        render: ({value}) => (<span style={{marginLeft: 20, fontSize: 14}}>{value}</span>),
      },
      {
        name: 'email', header: 'Email *', minWidth: tableCSS.email.width, flex: 1,
        headerProps: {
          className: "table-header"
        },
        render: ({value}) => (<span style={{marginLeft: 20, fontSize: 14}}>{value}</span>),
      },
      {
        name: 'invite',
        header: <span title={"Confirm your invite"}>Invite *</span>,
        headerEllipsis: false,
        editable: false,
        // @ts-ignore
        render: ({value, data}) => {
          let checked = true;
          if (value === "No") checked = false;
          return <div
            title={"If you can't select this box, please make sure this email is unique and that all fields are filled."}
            style={{display: 'flex', justifyContent: 'center', textAlign: 'center', fontSize: 14}}
            >
            <Checkbox
              id={value} checked={checked}
              onChange={(b) => updateTableDataField(b ? "Yes" : "No", data.id, "invite")}/></div>
        },
        width: tableCSS.remove.width,
        headerProps: {
          className: "table-header"
        }
      }
    ]

    useEffect(() => {
      let emailList: string[] = [];
      let uniqueEmailList: string[] = [];
      let difference: boolean = false;

      dataSource.forEach((invitee) => emailList.push(invitee.email));

      let newDataSource: jsonParseDataType[] = dataSource.map((invitee) => {
        if (flagToClean === false && (invitee.firstName === "" || invitee.lastName === "" || invitee.email === "")) {
          invitee.invite = "No";
        }
        if (invitee.email === "") {
        } else if (uniqueEmailList.indexOf(invitee.email) === -1) {
          uniqueEmailList.push(invitee.email);
        } else if (invitee.invite === "Yes") {
          invitee.invite = "No";
          difference = true;
        }
        return invitee;
      });
      if (difference) {
        setDataSource(newDataSource);
        setDuplicateEmailMessage(true);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataSource])

    useEffect(() => {
      if (uploadedData.length > 0) {

        setDataSource(uploadedData);

        let dirty = false;
        const fn: nameChecking[] = [];
        const sn: nameChecking[] = [];
        const e: emailChecking[] = [];

        uploadedData.forEach((dataElement) => {
          if (!dataElement.firstName || dataElement?.firstName?.length === 0 || !dataElement?.firstName.trim()) {
            fn.push({
              rowId: dataElement.id,
              column: "firstName",
              fieldValue: dataElement.firstName,
            });
            dirty = true;
          }

          if (!dataElement.lastName || dataElement?.lastName?.length === 0 || !dataElement?.lastName.trim()) {
            sn.push({
              rowId: dataElement.id,
              column: "lastName",
              fieldValue: dataElement.lastName,
            });
            dirty = true;
          }

          if (dataElement.email || (!dataElement.email && (dataElement.lastName || dataElement.firstName))) {
            if (isNotEmail(dataElement.email)) {
              e.push({
                rowId: dataElement.id,
                column: "email",
                fieldValue: dataElement.email,
              });
              dirty = true;
            }
          }

        });

        if (dirty) {
          setDataToClean({
            checkingFirstName: fn ?? [],
            checkingSecondName: sn ?? [],
            checkingEmails: e ?? [],
          });
        }

        setFlagToClean(dirty);

      }

    }, [uploadedData]);

    const onEditComplete = useCallback(({value, columnId, rowId}: any) => {
      const data: (jsonParseDataType)[] = [...dataSource];
      // best practice
      // 1. check if rowId < data.length
      // 2. columnId is property of jsonParseDataType
      if ((columnId === "firstName" || columnId === "lastName") && dataSource[rowId].invite === "Yes") {
        if (!stringCharExists(value)) {
          // @ts-ignore
          gridRef?.current.startEdit({rowIndex: rowId, columnId: columnId});
          errorToast(`Value cannot be empty, please fix it.`);
          return
        }
      }

      if (columnId === "email" && dataSource[rowId].invite === "Yes") {
        if (isNotEmail(value)) {
          // @ts-ignore
          gridRef?.current.startEdit({rowIndex: rowId, columnId: columnId});
          errorToast(`${value} is not a valid email address, please fix it.`);
          return;
        }
      }
      // @ts-ignore
      data[rowId][columnId] = value;
      // @ts-ignore
      setDataSource(data);
    }, [dataSource, errorToast, gridRef]);

    const updateTableDataField = (value: any, rowId: number, columnId: string) => {
      const data = [...dataSource];
      // @ts-ignore
      data[rowId][columnId] = value;
      setDataSource(data);
    }

    const inviteUsers = useCallback(async () => {
      const invitesToSend: any[] = [];
      let send: boolean = false;

      dataSource.forEach((invitee) => {
        if (invitee.invite === "Yes") {

          invitesToSend.push({
            firstName: invitee.firstName.toString(),
            lastName: invitee.lastName.toString(),
            email: invitee.email
          });

          send = true;
        }
      });

      if (send) {
        try {
          const CHUNK_LIMIT_LENGTH = 15;
          if (invitesToSend.length && invitesToSend.length > CHUNK_LIMIT_LENGTH) {
            const chunk: any[][] = [];
            for (let i = 0; i < invitesToSend.length; i += CHUNK_LIMIT_LENGTH) {
              chunk.push(invitesToSend.slice(i, i + CHUNK_LIMIT_LENGTH));
            }
            const res = await Promise.all(chunk.map(invites => bulkInviteUsers({variables: {input: invites}})));
            if (res.length === chunk.length) {
              setSuccess(true);
              successToast('Your Invitations have been Successfully Sent!');
            }
          } else {
            const res = await bulkInviteUsers({variables: {input: invitesToSend}});
            if (res?.data?.bulkInviteUsersAsEducator?.length) {
              setSuccess(true);
              successToast('Your Invitations have been Successfully Sent!');
            }
          }
        } catch (e: any) {
          errorToast(e?.message || '');
        }
      }
    }, [bulkInviteUsers, dataSource, successToast, errorToast]);

    if (flagToClean === null) {
      return <>
        <div style={{marginTop: 20, marginBottom: 50}}>Checking Validation...</div>
        <Spinner/>
      </>;
    }

    const closeAndClean = () => {
      setFlagToClean(null);
      setDataSource([]);
      setDataToClean({
        checkingEmails: [],
        checkingSecondName: [],
        checkingFirstName: []
      });
      setSuccess(false);
      closeSlide();
    }

    return (
      <>
        {flagToClean && !success ?
          <h3 className={styles.bulkInviteSubTitle} id="data-cleaning">We will now
            guide you through cleaning of your data.</h3>
          : success ?
            <h3 className={styles.bulkInviteSubTitle}>We have completed your Bulk
              Contact Invitation </h3> :
            <h3 className={styles.bulkInviteSubTitle}>Send Bulk Invite</h3>
        }
        {!success && !bulkInviteProcessing &&
        <div className={styles.noteText}>(Optional) Double click to edit fields</div>}
        {flagToClean && !success &&
        <FixDataWizard
          updateTableDataField={updateTableDataField}
          dataToClean={dataToClean}
          setTourEnd={setFlagToClean}
          dataSource={dataSource}
        />
        }
        {flagToClean ? (
          <div className={styles.table}>
            <div className={styles.header}>
              <div style={{width: tableCSS.id.width}}>ID</div>
              <div style={{minWidth: tableCSS.firstName.width, flex: 1}}>First Name *</div>
              <div style={{minWidth: tableCSS.secondName.width, flex: 1}}>Second Name *</div>
              <div style={{minWidth: tableCSS.email.width, flex: 1}}>Email *</div>
              <div style={{width: tableCSS.remove.width}}>Invite *</div>
            </div>
            <div className={styles.tableBody} id={"bulkInviteTable"}>
              {
                dataSource.map((x, i) => (
                    <div key={i} className={styles.rows}>
                      <div style={{width: tableCSS.id.width}}><span>{x.id}</span></div>
                      <div className={`firstName-${x.id}`} id={`firstName-${x.id}`}
                           style={{minWidth: tableCSS.firstName.width, flex: 1}}><span>{x.firstName}</span>
                      </div>
                      <div className={`lastName-${x.id}`} id={`lastName-${x.id}`}
                           style={{minWidth: tableCSS.secondName.width, flex: 1}}><span>{x.lastName}</span>
                      </div>
                      <div className={`email-${x.id}`} id={`email-${x.id}`}
                           style={{minWidth: tableCSS.email.width, flex: 1}}><span>{x.email}</span>
                      </div>
                      <div style={{width: tableCSS.remove.width}}><span>{x.invite}</span></div>
                    </div>
                  )
                )
              }
            </div>
          </div>
        ) : (
          <div style={{position: "relative"}}>
            {(success || bulkInviteProcessing) &&
            <div style={{
              position: "absolute",
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(255,255,255, 0.8)",
              zIndex: 1,
              textAlign: "center"
            }}>
             <div style={{
               position: "absolute",
               top: "50%",
               left: "50%",
               transform: "translate(-50%, -50%)",
               fontSize: "24px",
             }}>{success && "Bulk Upload Completed!"}{bulkInviteProcessing && (
               <div>
                 <div style={{marginTop: 20, marginBottom: 50}}>Bulk Uploading Data... Please wait.</div>
                 <Spinner/>
               </div>
             )}
             </div>
            </div>}
            <ReactDataGrid
              onReady={(gridData) => {
                setGridRef(gridData);
              }}
              reorderColumns={false}
              showColumnMenuTool={false}
              rowClassName={rowClassName}
              sortable={false}
              headerHeight={60}
              idProperty="id"
              pagination={false}
              style={gridStyle}
              onEditComplete={onEditComplete}
              editable={true}
              columns={bulkUploadColumns}
              dataSource={dataSource}
              activateRowOnFocus allowGroupSplitOnReorder allowUnsort autoCheckboxColumn
              autoFocusOnEditComplete
              cellSelectionByIndex checkboxOnlyRowSelect checkboxSelectEnableShiftKey checkResizeDelay={0}
            />
          </div>
        )
        }
        {duplicateEmailMessage && !success && !bulkInviteProcessing &&
        <div style={{color: "red", marginTop: 20}}><span style={{fontWeight: 600}}>Please note:</span> You have
         duplicate email address invites. You
         will not be able to select the "Invite"
         checkbox on the right and invite the same email address twice.</div>}
        <div style={{width: "100%", marginTop: "20px"}}>
          {success ? <Button
              color={"green"}
              onClick={closeAndClean}
              className={styles.inviteBtn}
              label={`Close Bulk Upload`}/> :
            <div style={{display: "flex"}}>
              <Button
                color={"red"}
                disabled={flagToClean || bulkInviteProcessing}
                onClick={closeAndClean}
                className={styles.inviteBtn}
                label={bulkInviteProcessing ? '' : `CANCEL BULK UPLOAD`}/>
              <Button
                disabled={flagToClean || dataSource.length === 0 || bulkInviteProcessing || !dataSource.some(invitee => invitee.invite === "Yes")}
                onClick={inviteUsers}
                className={styles.inviteBtnRight}
                label={bulkInviteProcessing ? '' : `Invite ${dataSource.filter((invitees: any) => invitees.invite === "Yes").length} Users`}/>
            </div>}
        </div>
      </>
    );
  }
;
